import React, { useState, useCallback } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { useTranslation } from "react-i18next";
import { FaHeadphonesSimple, FaChevronDown } from "react-icons/fa6";
import AudioPlayer from "../AudioPlayer";
import { useNavigate } from "react-router-dom";

interface CarDetailsContentProps {
  car: any;
  lang: string;
  translation: any;
  onInterest: () => void;
}

const CarDetailsContent: React.FC<CarDetailsContentProps> = ({
  car,
  translation,
}) => {
  const { t } = useTranslation();
  const [isOpen, setIsOpen] = useState(false);
  const navigate = useNavigate();

  // ✅ Gestion du bouton d'intérêt : Redirection avec message pré-rempli
  const handleInterestClick = useCallback(() => {
    const carTitle = car?.title || "un véhicule";
    const message = encodeURIComponent(
      `Bonjour, je suis intéressé(e) par le véhicule : ${carTitle}. Pouvez-vous me donner plus d'informations ?`
    );

    navigate(`/contact?message=${message}`);
  }, [navigate, car]);

  return (
    <>
      <div className="car-details-description">
        <p>
          <strong>
            {t("carDetails.price")}: {translation.price}
          </strong>
        </p>
        <p className="car-details-subtitle">
          {t("carDetails.year")}: {car.year} | {t("carDetails.power")}:{" "}
          {car.power}
        </p>
        <p>{translation.description}</p>
        <br />

        {/* Immersion sonore */}
        <div className="car-sound-immersive">
          <motion.div
            className="immersive-banner"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, ease: "easeOut" }}
          >
            <FaHeadphonesSimple size={40} />
            <h3>{t("carDetails.carSoundImmersion")}</h3>
            <p>{t("carDetails.carSoundTip")}</p>
            <motion.div className="audio-player-container">
              <AudioPlayer src={car.soundUrl} />
            </motion.div>
          </motion.div>
        </div>
        {/* Bouton d'intérêt */}
        <div className="car-details-interest">
          <motion.button
            className="interest-button"
            onClick={handleInterestClick}
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
          >
            {t("carDetails.interestButton")}
          </motion.button>
        </div>
        {/* AutoScout24 Link */}
        <div className="car-details-autoscoot">
          <strong>
            <h3>Retrouvez le véhicule également sur </h3>
          </strong>
          <a
            href={car.linkAutoScout24}
            target="_blank"
            rel="noopener noreferrer"
          >
            <motion.button
              className="car-details-autoscoot-button"
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
            >
              <img src="/images/logo/autoscoot24.svg" alt="logo autoscoot" /> →
            </motion.button>
          </a>
        </div>
        {/* Menu déroulant des  spécifications */}
        <div className="dropdown-container">
          <motion.button
            className="dropdown-toggle"
            onClick={() => setIsOpen((prev) => !prev)}
          >
            {t("carDetails.specification")}{" "}
            <FaChevronDown className={`chevron ${isOpen ? "open" : ""}`} />
          </motion.button>

          <AnimatePresence>
            {isOpen && (
              <motion.ul
                className="dropdown-menu"
                initial={{ opacity: 0, height: 0 }}
                animate={{ opacity: 1, height: "auto" }}
                exit={{ opacity: 0, height: 0 }}
                transition={{ duration: 0.4, ease: "easeOut" }}
              >
                <li>
                  {t("carDetails.driveType")}: {translation.driveType}
                </li>
                <li>
                  {t("carDetails.engine")}: {car.engine}
                </li>
                <li>
                  {t("carDetails.transmission")}: {car.transmission}
                </li>
                <li>
                  {t("carDetails.maxTorque")}: {car.maxTorque}
                </li>
                <li>
                  {t("carDetails.topSpeed")}: {car.topSpeed}
                </li>
                <li>
                  {t("carDetails.acceleration")}: {car.acceleration}
                </li>
                <li>
                  {t("carDetails.exteriorColor")}: {translation.exteriorColor}
                </li>
                <li>
                  {t("carDetails.interiorColor")}: {translation.interiorColor}
                </li>
              </motion.ul>
            )}
          </AnimatePresence>
        </div>
      </div>
    </>
  );
};

export default CarDetailsContent;
